import React, { useState, useEffect } from 'react';
import { X, UploadCloud, Target, CheckCircle2, Banknote, Info, Loader2 } from 'lucide-react';
import styles from './AddExpenseDrawer.module.css';

const categoryDB = {
    'Inventory': ['Hair Products', 'Skin Products', 'Makeup', 'Disposables', 'Tools & Equipment', 'Retail Stock'],
    'Maintenance': ['AC Servicing', 'Plumbing', 'Deep Cleaning', 'Electrical', 'Furniture Repair', 'Pest Control'],
    'Marketing': ['FB/Insta Ads', 'Influencer Collab', 'Pamphlets/Print', 'SMS Campaigns', 'SEO/Website'],
    'Utilities': ['Electricity', 'Water', 'Internet', 'Phone/Mobile', 'Software Subscriptions'],
    'Operations': ['Staff Tea/Welfare', 'Travel/Transport', 'Rent', 'Legal/Licensing', 'Office Supplies'],
    'Payroll/HR': ['Salary Advances', 'Commission Payouts', 'Training', 'Recruitment', 'Uniforms'],
};

// Indian shop date (the same day the cash drawer and daily report use)
const getLocalTodayString = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);

const emptyForm = () => ({
    type: 'Settled', date: getLocalTodayString(), invoiceNo: '',
    category: '', customCategory: '', subcategory: '', customSubcategory: '',
    amount: '', payMode: 'UPI', desc: '',
});

const AddExpenseDrawer = ({ isOpen, onClose, onSubmit, allowBackdate }) => {
    const todayStr = getLocalTodayString();
    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // fresh form with today's date every time the drawer opens
    useEffect(() => { if (isOpen) { setFormData(emptyForm()); setSaving(false); } }, [isOpen]);

    if (!isOpen) return null;

    const getDateProps = () => {
        if (formData.type === 'Settled') {
            if (!allowBackdate) {
                return { min: todayStr, max: todayStr, readOnly: true, className: `${styles.input} ${styles.readOnlyInput}`, title: 'Locked to today by your access.' };
            }
            return { max: todayStr, className: styles.input };   // paid: today or earlier
        }
        return { min: todayStr, className: styles.input };       // planned: today or later
    };
    const dateProps = getDateProps();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const finalCategory = formData.category === 'ADD_CUSTOM' ? formData.customCategory.trim() : formData.category;
        const finalSubCategory = formData.subcategory === 'ADD_CUSTOM' ? formData.customSubcategory.trim() : formData.subcategory;
        const amount = Number(formData.amount);
        if (!finalCategory || !(amount > 0)) { alert('Choose a category and enter an amount greater than zero.'); return; }

        setSaving(true);
        // the page returns true when the server saved it; the form is cleared only then
        const ok = await onSubmit({
            type: formData.type,
            invoiceNo: formData.invoiceNo.trim(),
            date: formData.date,
            category: finalCategory,
            subcategory: finalSubCategory || 'General',
            amount,
            payMode: formData.payMode,
            desc: formData.desc.trim(),
        });
        setSaving(false);
        if (ok !== false) setFormData(emptyForm());
    };

    const paidInCash = formData.type === 'Settled' && formData.payMode === 'Cash';

    return (
        <>
            <div className={styles.overlay} onClick={() => !saving && onClose()} />
            <div className={styles.drawer} role="dialog" aria-modal="true" aria-label="Record expense">
                <div className={styles.header}>
                    <div>
                        <h3>Record Expense</h3>
                        <p>Add money spent, or plan a future budget.</p>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} disabled={saving} aria-label="Close"><X size={16} /></button>
                </div>

                <div className={styles.body}>
                    <form id="expenseForm" onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.typeSelector}>
                            <label className={`${styles.typeOption} ${formData.type === 'Settled' ? styles.typeActive : ''}`}>
                                <input type="radio" name="expType" checked={formData.type === 'Settled'} onChange={() => setFormData({ ...formData, type: 'Settled', date: todayStr })} />
                                <CheckCircle2 size={14} /> Paid / Settled
                            </label>
                            <label className={`${styles.typeOption} ${formData.type === 'Planned' ? styles.typeActive : ''}`}>
                                <input type="radio" name="expType" checked={formData.type === 'Planned'} onChange={() => setFormData({ ...formData, type: 'Planned', date: todayStr })} />
                                <Target size={14} /> Planned Budget
                            </label>
                        </div>

                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label>{formData.type === 'Planned' ? 'Planned Date' : 'Payment Date'}</label>
                                <input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => { if (!dateProps.readOnly) setFormData({ ...formData, date: e.target.value }); }}
                                    {...dateProps}
                                    required
                                />
                            </div>
                            <div className={styles.field}>
                                <label>Invoice / Ref No.</label>
                                <input type="text" placeholder="e.g. BILL-102" value={formData.invoiceNo} onChange={(e) => setFormData({ ...formData, invoiceNo: e.target.value })} className={styles.input} />
                            </div>
                        </div>

                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label>Category</label>
                                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategory: '' })} className={styles.select} required>
                                    <option value="">Select Category...</option>
                                    {Object.keys(categoryDB).map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                                    <option value="ADD_CUSTOM">+ Custom Category...</option>
                                </select>
                            </div>
                            <div className={styles.field}>
                                <label>Sub-Category</label>
                                <select value={formData.subcategory} onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })} className={styles.select} disabled={!formData.category}>
                                    <option value="">Select Sub-category...</option>
                                    {categoryDB[formData.category]?.map((sub) => <option key={sub} value={sub}>{sub}</option>)}
                                    <option value="ADD_CUSTOM">+ Custom Sub-category...</option>
                                </select>
                            </div>
                        </div>

                        {formData.category === 'ADD_CUSTOM' && (
                            <div className={styles.field}>
                                <label>Type Custom Category</label>
                                <input type="text" placeholder="E.g. Festivities" value={formData.customCategory} onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })} className={styles.input} required />
                            </div>
                        )}
                        {formData.subcategory === 'ADD_CUSTOM' && (
                            <div className={styles.field}>
                                <label>Type Custom Sub-category</label>
                                <input type="text" placeholder="E.g. Diwali Sweets" value={formData.customSubcategory} onChange={(e) => setFormData({ ...formData, customSubcategory: e.target.value })} className={styles.input} required />
                            </div>
                        )}

                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label>{formData.type === 'Planned' ? 'Estimated Amount (₹)' : 'Total Paid (₹)'}</label>
                                <input type="number" min="0" step="0.01" inputMode="decimal" placeholder="0.00" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className={`${styles.input} ${styles.amtInput}`} required />
                            </div>
                            <div className={styles.field}>
                                <label>Payment Mode</label>
                                <select value={formData.payMode} onChange={(e) => setFormData({ ...formData, payMode: e.target.value })} className={styles.select}>
                                    <option value="Cash">Cash</option>
                                    <option value="UPI">UPI (GPay/PhonePe)</option>
                                    <option value="Credit Card">Credit Card</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                </select>
                            </div>
                        </div>

                        {paidInCash && (
                            <div className={`${styles.hint} ${styles.hintCash}`}>
                                <Banknote size={15} />
                                <span>Paid from the cash drawer. {formData.amount ? `₹${Number(formData.amount).toLocaleString('en-IN')} ` : 'This amount '}will be taken off the cash that should be in the drawer today.</span>
                            </div>
                        )}
                        {formData.type === 'Planned' && (
                            <div className={`${styles.hint} ${styles.hintPlan}`}>
                                <Info size={15} />
                                <span>A planned budget is not money spent. It does not change today's cash or the day result.</span>
                            </div>
                        )}

                        <div className={styles.field}>
                            <label>Expense Description</label>
                            <textarea placeholder="Briefly describe this expense..." value={formData.desc} onChange={(e) => setFormData({ ...formData, desc: e.target.value })} className={styles.textarea} rows="2" required />
                        </div>

                        <div className={styles.uploadArea}>
                            <UploadCloud size={20} className={styles.uploadIcon} />
                            <strong>Upload Receipt</strong>
                            <span>PNG/PDF up to 5MB</span>
                        </div>
                    </form>
                </div>

                <div className={styles.footer}>
                    <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
                    <button type="submit" form="expenseForm" className={styles.submitBtn} disabled={saving}>
                        {saving ? <><Loader2 size={14} className={styles.spin} /> Saving…</> : 'Save expense'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default AddExpenseDrawer;
